import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Skeleton } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { RetryButton } from "./RetryButton";

const ROW_WIDTHS = ["w-full", "w-11/12", "w-4/6", "w-5/6", "w-3/6"];

export interface LoadingStateProps {
  label?: string;
  /** "spinner" for a quick fetch, "skeleton" for a list/detail placeholder
   * that roughly previews the shape of what's loading. */
  variant?: "spinner" | "skeleton";
  /** Skeleton row count (variant="skeleton" only). */
  rows?: number;
  /** After this many ms, flip to a "still working…" affordance (+ retry, if
   * given) instead of spinning silently forever — a hung request should never
   * look identical to a fast one. */
  timeoutMs?: number;
  onRetry?: () => void;
  className?: string;
}

/** "This is loading" — the shared shape for every fetch-in-flight view. */
export function LoadingState({
  label = "Loading…",
  variant = "spinner",
  rows = 3,
  timeoutMs,
  onRetry,
  className,
}: LoadingStateProps) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!timeoutMs) return;
    setStuck(false);
    const t = window.setTimeout(() => setStuck(true), timeoutMs);
    return () => window.clearTimeout(t);
  }, [timeoutMs]);

  if (variant === "skeleton") {
    return (
      <div
        className={cn("flex flex-col gap-2 p-3", className)}
        role="status"
        aria-label={label}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className={cn("h-4", ROW_WIDTHS[i % ROW_WIDTHS.length])} />
        ))}
        {stuck && <StillWorking onRetry={onRetry} />}
      </div>
    );
  }

  return (
    <div
      className={cn("flex flex-col items-center justify-center gap-2 p-6 text-center", className)}
      role="status"
    >
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      {label && <p className="text-[13px] text-muted-foreground">{label}</p>}
      {stuck && <StillWorking onRetry={onRetry} />}
    </div>
  );
}

function StillWorking({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="mt-2 flex flex-col items-center gap-2">
      <p className="text-[12px] text-muted-foreground">This is taking longer than expected.</p>
      {onRetry && <RetryButton onRetry={onRetry} />}
    </div>
  );
}
